import {
  Box,
  ButtonGroup,
  Collapsible,
  Flex,
  Heading,
  IconButton,
  Pagination,
  Text,
} from '@chakra-ui/react';
import ExpandableSearchButton from '@components/common/ExpandableSearchButton';
import { InfoTip } from '@components/ui/toggle-tip';
import ContractDetailView from '@features/explore/ContractDetailView';
import { allContracts, Contract } from 'helpers/fakeData';
import { memo, useCallback, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import RawArrowIcon from 'vectors/Arrowhead';

const COL_WIDTHS = {
  arrow: { base: '32px', sm: '40px' },
  forwardPrice: '140px',
  settlementDate: '160px',
  contractSize: '140px',
  fill: '70px',
};

const firmContracts = allContracts.filter((c) => c.contractType === 'firm');

type CommodityId = string;
const ContractTable = () => {
  const [expanded, setExpanded] = useState<CommodityId[]>([]);
  const [query, setQuery] = useState('');

  const handleExpand = useCallback(
    (id: CommodityId) => () => {
      if (expanded.includes(id)) {
        setExpanded((prev) => prev.filter((item) => item !== id));
        return;
      }
      setExpanded((prev) => [...prev, id]);
    },
    [expanded],
  );

  return (
    <Flex direction="column" gap={2}>
      <Flex justifyContent="space-between" alignItems="center">
        <Heading>Contracts</Heading>
        <ExpandableSearchButton query={query} setQuery={setQuery} />
      </Flex>
      <TableHeader />
      <Flex direction="column">
        {firmContracts.map((item, index) => {
          const isExpanded = expanded.includes(item.id);
          const isFirst = index === 0;
          const isLast = index === firmContracts.length - 1;
          return (
            <ContractRow
              key={item.id}
              contract={item}
              isExpanded={isExpanded}
              isFirst={isFirst}
              isLast={isLast}
              onToggle={handleExpand(item.id)}
            />
          );
        })}
      </Flex>
      <Pagination.Root count={50} pageSize={5} defaultPage={1} maxW="240px" ml="auto">
        <ButtonGroup variant="ghost" size="sm" w="full">
          <Pagination.PageText format="long" flex="1" />
          <Pagination.PrevTrigger asChild>
            <IconButton>
              <LuChevronLeft />
            </IconButton>
          </Pagination.PrevTrigger>
          <Pagination.NextTrigger asChild>
            <IconButton>
              <LuChevronRight />
            </IconButton>
          </Pagination.NextTrigger>
        </ButtonGroup>
      </Pagination.Root>
    </Flex>
  );
};

interface ContractRowProps {
  contract: Contract;
  isExpanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
}

const ContractRow = memo(
  ({ contract, isExpanded, isFirst, isLast, onToggle }: ContractRowProps) => {
    const isLastAndClosed = isLast && !isExpanded;

    return (
      <Box>
        <Flex
          bgColor="surface.muted"
          _hover={{ bgColor: 'surface.accent' }}
          cursor="pointer"
          onClick={onToggle}
          alignItems="center"
          borderTopLeftRadius={isFirst ? 'xl' : 'none'}
          borderTopRightRadius={isFirst ? 'xl' : 'none'}
          borderBottomLeftRadius={isLastAndClosed ? 'xl' : 'none'}
          borderBottomRightRadius={isLastAndClosed ? 'xl' : 'none'}
          px={{ base: 2, sm: 4 }}
          py={3}
          gap={{ base: 2, sm: 4 }}
        >
          <Box w={COL_WIDTHS.arrow} flexShrink={0}>
            <IconButton variant="ghost" size="2xs">
              <RawArrowIcon direction={isExpanded ? 'down' : 'right'} boxSize="8px" />
            </IconButton>
          </Box>
          <Flex direction="column" flex={1} minW={0} gap={0.5}>
            <Flex gap={2} alignItems="center">
              <Text fontSize="sm" fontWeight="medium">
                {contract.buyerName}
              </Text>
            </Flex>
            <Text fontSize="xs" color="fg.subtle" hideFrom="md">
              {contract.settlementDate}
            </Text>
          </Flex>
          <Text
            fontSize="sm"
            fontWeight="medium"
            textAlign="end"
            w={{ base: 'auto', md: COL_WIDTHS.forwardPrice }}
            flexShrink={0}
          >
            {`GHS${contract.forwardPrice}`}
          </Text>
          <Text
            fontSize="sm"
            fontWeight="medium"
            textAlign="end"
            w={COL_WIDTHS.settlementDate}
            flexShrink={0}
            hideBelow="md"
          >
            {contract.settlementDate}
          </Text>
          <Text
            fontSize="sm"
            fontWeight="medium"
            textAlign="end"
            w={COL_WIDTHS.contractSize}
            flexShrink={0}
            hideBelow="md"
          >
            {contract.contractSize}
          </Text>
          <Text
            fontSize="sm"
            fontWeight="medium"
            textAlign="end"
            w={COL_WIDTHS.fill}
            flexShrink={0}
            hideBelow="md"
          >
            {contract.fillPercentage}%
          </Text>
        </Flex>
        <Collapsible.Root open={isExpanded} lazyMount unmountOnExit>
          <Collapsible.Content>
            <Box
              bgColor="surface.muted"
              p={4}
              borderBottomLeftRadius={isLast ? 'xl' : 'none'}
              borderBottomRightRadius={isLast ? 'xl' : 'none'}
            >
              <Box borderRadius="xl" bg="surface.background" overflow="hidden">
                <ContractDetailView contract={contract} />
              </Box>
            </Box>
          </Collapsible.Content>
        </Collapsible.Root>
      </Box>
    );
  },
);
ContractRow.displayName = 'ContractRow';

const TableHeader = () => (
  <Flex px={{ base: 2, sm: 4 }} py={2} gap={{ base: 2, sm: 4 }} alignItems="center" hideBelow="md">
    <Box w={COL_WIDTHS.arrow} flexShrink={0} />
    <Text fontSize="sm" fontWeight="medium" color="text.secondary" flex={1}>
      Buyer
    </Text>
    <Flex
      direction="row"
      gap={1}
      alignItems="center"
      justifyContent="flex-end"
      w={COL_WIDTHS.forwardPrice}
      flexShrink={0}
      whiteSpace="nowrap"
    >
      <Text fontSize="sm" fontWeight="medium" color="text.secondary">
        Forward Price
      </Text>
      <InfoTip>
        The forward price is the predetermined, locked-in price for buying or selling
      </InfoTip>
    </Flex>
    <Flex
      direction="row"
      gap={1}
      alignItems="center"
      justifyContent="flex-end"
      w={COL_WIDTHS.settlementDate}
      flexShrink={0}
      whiteSpace="nowrap"
    >
      <Text fontSize="sm" fontWeight="medium" color="text.secondary">
        Settlement Date
      </Text>
      <InfoTip>
        Settlement date is the date on which the contract is settled and cash settlements are
        executed
      </InfoTip>
    </Flex>
    <Flex
      direction="row"
      gap={1}
      alignItems="center"
      justifyContent="flex-end"
      w={COL_WIDTHS.contractSize}
      flexShrink={0}
      whiteSpace="nowrap"
    >
      <Text fontSize="sm" fontWeight="medium" color="text.secondary">
        Contract Size
      </Text>
      <InfoTip>Contract size is the total amount of commodity being bought or sold</InfoTip>
    </Flex>
    <Flex
      direction="row"
      gap={1}
      alignItems="center"
      justifyContent="flex-end"
      w={COL_WIDTHS.fill}
      flexShrink={0}
    >
      <Text fontSize="sm" fontWeight="medium" color="text.secondary">
        Fill
      </Text>
      <InfoTip>Fill is the percentage of the contract that has been filled by suppliers</InfoTip>
    </Flex>
  </Flex>
);

export default memo(ContractTable);
